import type { ReactElement } from 'react';

import { Box, Button, Flex, Heading, IconButton, Popover } from '@rango-dev/whale';
import { useToast } from '@rango-dev/whale/lab';
import { useWidget } from '@rango-dev/widget-embedded';
import { Bell } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { WIDGET_BASE_ROUTE } from '../../constants/navigation';
import { getContainer } from '../../utils/generalFunctions/common';

import { NotificationsEmptyState } from './Notifications.EmptyState';
import { NotificationItem } from './Notifications.Item';
import styles from './Notifications.module.css';
import { UnreadNotificationsBadge } from './UnreadNotificationsBadge';

const AUTO_HIDE_NOTIFICATION_INFO_DURATION = 10_000;
const ICON_SIZE = 18;
const SWAP_REQUEST_ID_SEGMENT_INDEX = 2;
const POPOVER_SIDE_OFFSET = 8;
const POPOVER_COLLISION_PADDING = 8;
const PANEL_ACCENT_COLOR = 'blue';

export function Notifications(): ReactElement {
  const {
    notifications: { list, clearAll },
  } = useWidget();

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleSelect = (requestId: string) => {
    const segments = pathname.split('/').filter(Boolean);
    if (segments[SWAP_REQUEST_ID_SEGMENT_INDEX] === requestId) {
      toast({
        title: t('You’re already on this swap page.'),
        type: 'info',
        duration: AUTO_HIDE_NOTIFICATION_INFO_DURATION,
      });

      return;
    }
    navigate({
      pathname: `${WIDGET_BASE_ROUTE}/swaps/${requestId}`,
      search: `?${searchParams.toString()}`,
    });
  };

  return (
    <Popover.Root>
      <Popover.Trigger>
        <IconButton
          id="header-notification-icon-btn"
          aria-label={t('Notifications')}
          variant="ghost"
          color="cyan"
          radius="full"
          size="3"
          className={styles.trigger}>
          <Bell size={ICON_SIZE} />
          <UnreadNotificationsBadge count={list.length} />
        </IconButton>
      </Popover.Trigger>
      <Popover.Content
        align="end"
        sideOffset={POPOVER_SIDE_OFFSET}
        collisionPadding={POPOVER_COLLISION_PADDING}
        container={getContainer()}
        width="400px"
        maxWidth="calc(100vw - var(--space-6))">
        <Box data-accent-color={PANEL_ACCENT_COLOR}>
          <Flex direction="column" gap="3">
            <Flex align="center" justify="between" gap="3">
              <Heading as="h2" size="3" weight="bold" className={styles.panelHeading}>
                {t('Notification')}
              </Heading>
              {list.length > 0 && (
                <Button
                  id="app-notification-content-clear-all-btn"
                  variant="ghost"
                  color="gray"
                  radius="full"
                  size="1"
                  onClick={clearAll}>
                  {t('Clear all')}
                </Button>
              )}
            </Flex>

            {list.length > 0 ? (
              <Flex direction="column" gap="2">
                {list.map((notification) => (
                  <NotificationItem
                    key={notification.requestId}
                    notification={notification}
                    onSelect={handleSelect}
                  />
                ))}
              </Flex>
            ) : (
              <NotificationsEmptyState />
            )}
          </Flex>
        </Box>
      </Popover.Content>
    </Popover.Root>
  );
}
